import type { ReactNode } from "react";

import { Controller, useWatch } from "react-hook-form";
import { useLocalization } from "@fluent/react";
import { ContentCard, TextInput } from "@narmi/design_system";

import InstitutionDetails from "./InstitutionDetails";
import InternationalPhoneInput from "./InternationalPhoneInput";
import styles from "./InternationalWireForm.module.scss";

import type { SwiftDestinationFormType } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import type { BeneficiaryBankDetails } from "byzantine/src/dbbl/businessLogic/modules/recipientDrawer";
import type { Control } from "react-hook-form";

// a bank that identifies accounts by phone number renames the account field to this
const PHONE_NUMBER_RENAME = "Phone number";

interface BeneficiaryBankFieldsProps {
  control: Control<SwiftDestinationFormType>;
  isCorrespondentBankEnabled: boolean;
  bank: BeneficiaryBankDetails;
  isLoadingBank: boolean;
  bicError?: string;
  // the API's first error for a field
  getFieldError: (name: string) => string | undefined;
  onBicBlur: (bic: string) => void;
  // a bank can ask for its branch's address
  renderBranchAddress: () => ReactNode;
}

const BeneficiaryBankFields = ({
  control,
  isCorrespondentBankEnabled,
  bank,
  isLoadingBank,
  bicError,
  getFieldError,
  onBicBlur,
  renderBranchAddress,
}: BeneficiaryBankFieldsProps) => {
  const { l10n } = useLocalization();
  // the phone inputs default their calling code from the BIC's country
  const bic = useWatch({ control, name: "bic" }) || "";

  const renderAdditionalField = ({
    name,
  }: API.SwiftDestinationAdditionalField) => {
    if (name === "branch_address") {
      return (
        <div className="margin--top--l" key={name}>
          {renderBranchAddress()}
        </div>
      );
    }
    return (
      <div className="margin--top--l" key={name}>
        <Controller
          name={`metadata.${name}`}
          control={control}
          render={({ field, fieldState: { error } }) => {
            const fieldError = error?.message || getFieldError(name);
            if (name === "phone") {
              return (
                <InternationalPhoneInput
                  inputRef={field.ref}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  value={String(field.value ?? "")}
                  error={fieldError}
                  bic={bic}
                />
              );
            }
            return (
              <TextInput
                {...field}
                label={
                  name === "bank_id"
                    ? l10n.getString(
                        "international-wire-drawer-bank-id-optional"
                      )
                    : bank.renameFields[name] || name
                }
                value={String(field.value || "")}
                error={fieldError}
              />
            );
          }}
        />
      </div>
    );
  };

  return (
    <ContentCard kind="plain" paddingSize="none">
      <p className={`fontColor--secondary ${styles.sectionSubHeader}`}>
        {l10n.getString("international-wire-drawer-beneficiary-bank-details")}
      </p>
      {isCorrespondentBankEnabled ? (
        <>
          <Controller
            name="metadata.bank_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                value={field.value || ""}
                field="metadata.bank_name"
                label={l10n.getString("international-wire-drawer-bank-name")}
                error={error?.message || getFieldError("bank_name")}
              />
            )}
          />
          <div className="margin--top--l">
            <Controller
              name="metadata.bank_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  value={field.value || ""}
                  field="metadata.bank_id"
                  label={l10n.getString(
                    "international-wire-drawer-bank-id-optional"
                  )}
                  error={error?.message || getFieldError("bank_id")}
                />
              )}
            />
          </div>
        </>
      ) : (
        <>
          <Controller
            name="bic"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                value={field.value || ""}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  field.onBlur();
                  onBicBlur(event.target.value);
                }}
                field="bic"
                label={l10n.getString("swift-bic")}
                error={error?.message || bicError}
              />
            )}
          />
          <InstitutionDetails
            isLoading={isLoadingBank}
            institution={bank.institution}
          />
        </>
      )}
      <div className="margin--top--l">
        <Controller
          name="account_identifier"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const fieldError =
              error?.message || getFieldError("account_identifier");
            if (bank.renameFields.account_identifier === PHONE_NUMBER_RENAME) {
              return (
                <InternationalPhoneInput
                  inputRef={field.ref}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  value={field.value}
                  error={fieldError}
                  bic={bic}
                />
              );
            }
            return (
              <TextInput
                {...field}
                value={field.value || ""}
                field="account_identifier"
                label={
                  bank.renameFields.account_identifier ||
                  l10n.getString("recipient-drawer-account-identifier-label")
                }
                error={fieldError}
              />
            );
          }}
        />
      </div>
      {bank.additionalFields.map(renderAdditionalField)}
    </ContentCard>
  );
};

export default BeneficiaryBankFields;
